// Fast Project API Service with Caching
// Optimized for production performance

import { supabase } from '../utils/supabaseClient';

// Use TEAM API for project operations (port 3001)
const API_BASE_URL = import.meta.env.VITE_TEAM_API_URL || 'https://homequest-api-server-3001.onrender.com';
const CACHE_KEY = 'homequest_projects_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Demo project ID
const DEMO_PROJECT_ID = '00000000-0000-0000-0000-000000000001';

// Check if we're in demo mode
function isDemoMode(): boolean {
  // Check if URL contains demo project ID
  const pathname = window.location.pathname;
  const isDemo = pathname.includes(DEMO_PROJECT_ID) || pathname === '/demo';
  console.log('🔍 isDemoMode check:', { pathname, DEMO_PROJECT_ID, isDemo });
  return isDemo;
}

// Get demo project data
function getDemoProjects() {
  return [{
    id: DEMO_PROJECT_ID,
    project_name: 'Lawrenceville Luxury Estate',
    address: '1250 Scenic Hwy, Lawrenceville, GA 30045',
    status: 'active',
    progress: 45,
    square_footage: 4850,
    total_budget: 850000,
    start_date: '2025-09-15',
    estimated_completion: '2026-03-15',
    notes: 'Custom luxury home with modern finishes. Currently in framing phase.',
    created_at: '2025-09-01T10:00:00Z',
    updated_at: new Date().toISOString()
  }];
}

interface CachedData {
  projects: any[];
  timestamp: number;
}

// Get cached projects if available
function getCachedProjects(): any[] | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp < CACHE_DURATION) {
      console.log('✅ Using cached projects (fast load)');
      return data.projects;
    }

    console.log('⏰ Cache expired, fetching new data');
    return null;
  } catch (e) {
    return null;
  }
}

// Save projects to cache
function cacheProjects(projects: any[]) {
  try {
    const data: CachedData = {
      projects,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    console.log(`✅ Cached ${projects.length} projects`);
  } catch (e) {
    console.error('Cache save error:', e);
  }
}

// Clear cache
export function clearProjectCache() {
  localStorage.removeItem(CACHE_KEY);
  console.log('🗑️ Project cache cleared');
}

// Get auth token from multiple sources
async function getAuthToken(): Promise<string | null> {
  // Try Supabase session first
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return session.access_token;
    }
  } catch (e) {
    console.error('Error getting session:', e);
  }

  // Fallback to localStorage token
  const tokenData = localStorage.getItem('sb-fbwmkkskdrvaipmkddwm-auth-token');
  if (tokenData) {
    try {
      const parsed = JSON.parse(tokenData);
      if (parsed.access_token) {
        return parsed.access_token;
      }
    } catch (e) {
      console.error('Error parsing token:', e);
    }
  }

  return null;
}

// Fetch projects from backend API
export async function fetchProjectsFromAPI(forceRefresh = false): Promise<{
  data?: any[];
  error?: string;
  fromCache?: boolean;
}> {
  // Check if we're in demo mode - return demo data immediately
  if (isDemoMode()) {
    console.log('🎭 Demo mode detected - returning demo projects');
    return { data: getDemoProjects(), fromCache: false };
  }

  // Check cache first unless forced refresh
  if (!forceRefresh) {
    const cached = getCachedProjects();
    if (cached) {
      return { data: cached, fromCache: true };
    }
  }

  try {
    // Get auth token
    const token = await getAuthToken();
    if (!token) {
      console.error('No auth token available');
      return { error: 'Please log in to view projects' };
    }

    console.log(`📡 Fetching projects from ${API_BASE_URL}/api/projects`);

    // Add timeout for slow backends
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

    const response = await fetch(`${API_BASE_URL}/api/projects`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API Error:', response.status, errorData);

      // If backend is down, try direct Supabase as fallback
      if (response.status >= 500) {
        console.log('⚠️ Backend error, falling back to Supabase direct');
        return fetchProjectsFromSupabase();
      }

      return {
        error: errorData.error || `Failed to fetch projects: ${response.status}`
      };
    }

    const result = await response.json();
    const projects = result.data || [];

    console.log(`✅ Fetched ${projects.length} projects from API`);

    // Cache the results
    cacheProjects(projects);

    return { data: projects };

  } catch (error: any) {
    console.error('Fetch error:', error);

    // If timeout or network error, try direct Supabase
    if (error.name === 'AbortError') {
      console.log('⏱️ API timeout, falling back to Supabase direct');
      return fetchProjectsFromSupabase();
    }

    // Check if we have cached data to show
    const cached = getCachedProjects();
    if (cached) {
      console.log('⚠️ Using stale cache due to error');
      return { data: cached, fromCache: true };
    }

    return { error: error.message || 'Failed to fetch projects' };
  }
}

// Direct Supabase fallback
async function fetchProjectsFromSupabase(): Promise<{
  data?: any[];
  error?: string;
}> {
  // Check if we're in demo mode - return demo data immediately
  if (isDemoMode()) {
    console.log('🎭 Demo mode detected - returning demo projects (from Supabase fallback)');
    return { data: getDemoProjects() };
  }

  try {
    console.log('📡 Fetching directly from Supabase...');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { error: 'Please log in to view projects' };
    }

    // Get user's team
    const { data: profile } = await supabase
      .from('profiles')
      .select('team_id, role')
      .eq('id', user.id)
      .single();

    // Build query
    let query = supabase
      .from('projects')
      .select(`
        *,
        buildings:buildings(count),
        construction_phases:construction_phases(
          id,
          phase,
          status,
          completion_percentage
        )
      `);

    if (profile?.team_id) {
      query = query.eq('team_id', profile.team_id);
    } else {
      query = query.eq('user_id', user.id);
    }

    const { data: projects, error } = await query;

    if (error) {
      console.error('Supabase error:', error);
      return { error: error.message };
    }

    // Process projects
    const processedProjects = (projects || []).map(project => {
      const phases = project.construction_phases || [];
      const totalProgress = phases.reduce((sum: number, phase: any) =>
        sum + (phase.completion_percentage || 0), 0
      );
      const averageProgress = phases.length > 0 ? totalProgress / phases.length : 0;

      return {
        ...project,
        progress: Math.round(averageProgress),
        phaseCount: phases.length,
        activePhase: phases.find((p: any) => p.status === 'in_progress')?.phase || 'Planning'
      };
    });

    console.log(`✅ Fetched ${processedProjects.length} projects from Supabase`);

    // Cache the results
    cacheProjects(processedProjects);

    return { data: processedProjects };

  } catch (error: any) {
    console.error('Supabase fallback error:', error);
    return { error: error.message || 'Failed to fetch projects from Supabase' };
  }
}

// Create a new project
export async function createProject(projectData: any): Promise<{
  data?: any;
  error?: string;
}> {
  // In demo mode, pretend to create project but don't actually do it
  if (isDemoMode()) {
    console.log('🎭 Demo mode - simulating project creation');
    return {
      error: 'Demo mode: Changes are not saved. Sign up to create real projects!'
    };
  }

  try {
    const token = await getAuthToken();
    if (!token) {
      return { error: 'Please log in to create projects' };
    }

    console.log('📤 Creating project via API...');

    const response = await fetch(`${API_BASE_URL}/api/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(projectData)
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Create project error:', result);
      return { error: result.error || 'Failed to create project' };
    }

    console.log('✅ Project created successfully');

    // Clear cache so new project shows up
    clearProjectCache();

    return { data: result.data };

  } catch (error: any) {
    console.error('Create project error:', error);
    return { error: error.message || 'Failed to create project' };
  }
}

// Keep backend warm to prevent cold starts
export function startKeepAlive() {
  console.log('🔥 Starting keep-alive for backend...');

  // Initial ping
  pingBackend();

  // Ping every 4 minutes
  setInterval(pingBackend, 4 * 60 * 1000);
}

async function pingBackend() {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('🟢 Backend is warm:', data.message);
    }
  } catch (e) {
    console.error('❌ Keep-alive ping failed:', e);
  }
}